import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDocumentFileType } from "./enums/enumDocumentFileType.generated";
import { EnumYesNoEither } from "./enums/enumYesNoEither.generated";

// Model Objects

export class JourneyHeaderReadOnly extends ModelObject<JourneyHeaderReadOnly> {

    /**
    * Indicates if the driver has checked and confirmed the vehicle is fit for purpose prior to departure.
    */
    public confirmedVehicleFitForPurpose: boolean = false;
    /**
    * Date of Despatch
    */
    public despatchDate: LocalDate = LocalDate.ofEpochDay(0);
    private _endedAt: Instant | null = null;
    /**
    * Timestamp of when the journey was ended.
    */
    public get endedAt(): Instant | null { return this._endedAt }
    public set endedAt(value: Instant | null) {
      this._endedAt = value === undefined ? null : value;
    }
    private _endedBy: Guid | null = null;
    /**
    * Id of the user who ended the journey.
    */
    public get endedBy(): Guid | null { return this._endedBy }
    public set endedBy(value: Guid | null) {
      this._endedBy = value === undefined ? null : value;
    }
    private _endOdometerReading: number | null = null;
    /**
    * The vehicle odometer reading at the end of the journey. Must be provided before the journey can end.
    */
    public get endOdometerReading(): number | null { return this._endOdometerReading }
    public set endOdometerReading(value: number | null) {
      this._endOdometerReading = value === undefined ? null : value;
    }
    /**
    * Indicates if the regular vehicle monthly checks were performed prior to departure.
    */
    public monthlyChecksPerformed: boolean = false;
    private _startedAt: Instant | null = null;
    /**
    * Timestamp of when the journey was started. Presence of a value implies journey has been started and is therefore locked unless it has also been ended.
    */
    public get startedAt(): Instant | null { return this._startedAt }
    public set startedAt(value: Instant | null) {
      this._startedAt = value === undefined ? null : value;
    }
    private _startedByUserId: Guid | null = null;
    /**
    * User who has started the journey. Journey is locked to the user until the journey is ended.
    */
    public get startedByUserId(): Guid | null { return this._startedByUserId }
    public set startedByUserId(value: Guid | null) {
      this._startedByUserId = value === undefined ? null : value;
    }
    private _startOdometerReading: number | null = null;
    /**
    * The vehicle odometer reading at the start of the journey. Must be provided before the journey can end.
    */
    public get startOdometerReading(): number | null { return this._startOdometerReading }
    public set startOdometerReading(value: number | null) {
      this._startOdometerReading = value === undefined ? null : value;
    }
    /**
    * Vehicle Id
    */
    public vehicleId: Guid = Guid.createEmpty();
    /**
    * Indicates if the regular vehicle weekly checks were performed prior to departure.
    */
    public weeklyChecksPerformed: boolean = false;
    /**
    * Indicates if the loading list was checked prior to departure.
    */
    public loadingListChecked: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.confirmedVehicleFitForPurpose = jsonSerializedObject.confirmedVehicleFitForPurpose;
          this.despatchDate = LocalDate.parse(jsonSerializedObject.despatchDate);
          this.endedAt = jsonSerializedObject.endedAt == null ? null : Instant.parse(jsonSerializedObject.endedAt);
          this.endedBy = jsonSerializedObject.endedBy == null ? null : Guid.parseOrEmpty(jsonSerializedObject.endedBy);
          this.endOdometerReading = jsonSerializedObject.endOdometerReading == null ? null : jsonSerializedObject.endOdometerReading;
          this.monthlyChecksPerformed = jsonSerializedObject.monthlyChecksPerformed;
          this.startedAt = jsonSerializedObject.startedAt == null ? null : Instant.parse(jsonSerializedObject.startedAt);
          this.startedByUserId = jsonSerializedObject.startedByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.startedByUserId);
          this.startOdometerReading = jsonSerializedObject.startOdometerReading == null ? null : jsonSerializedObject.startOdometerReading;
          this.vehicleId = Guid.parseOrEmpty(jsonSerializedObject.vehicleId);
          this.weeklyChecksPerformed = jsonSerializedObject.weeklyChecksPerformed;
          this.loadingListChecked = jsonSerializedObject.loadingListChecked;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.confirmedVehicleFitForPurpose = this.confirmedVehicleFitForPurpose;
        toRet.despatchDate = this.despatchDate.toString();
        toRet.endedAt = this.endedAt == null ? null : this.endedAt.toString();
        toRet.endedBy = this.endedBy == null ? null : this.endedBy.toString();
        toRet.endOdometerReading = this.endOdometerReading == null ? null : this.endOdometerReading;
        toRet.monthlyChecksPerformed = this.monthlyChecksPerformed;
        toRet.startedAt = this.startedAt == null ? null : this.startedAt.toString();
        toRet.startedByUserId = this.startedByUserId == null ? null : this.startedByUserId.toString();
        toRet.startOdometerReading = this.startOdometerReading == null ? null : this.startOdometerReading;
        toRet.vehicleId = this.vehicleId.toString();
        toRet.weeklyChecksPerformed = this.weeklyChecksPerformed;
        toRet.loadingListChecked = this.loadingListChecked;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "endedBy", rule: (v: any) => v == null || !v.isEmpty() || "JourneyHeaderReadOnly.EndedBy cannot be empty"},
            {field: "startedByUserId", rule: (v: any) => v == null || !v.isEmpty() || "JourneyHeaderReadOnly.StartedByUserId cannot be empty"},
            {field: "vehicleId", rule: (v: any) => !v.isEmpty() || "JourneyHeaderReadOnly.VehicleId cannot be empty"},
    ];
}


export class JourneyHeader extends ModelObject<JourneyHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Identifies if journey is active. An inactive journey is not intended to run and indicates an explicit user decision for this.
    */
    public isActive: boolean = false;
    /**
    * Identifies if journey is locked for planning, meaning it will not take further part in optimisation.
    */
    public isPlanningLocked: boolean = false;
    /**
    * Expected Time the Journey will start, assumed to be in the Tenant's Timezone, on the DespatchDate
    */
    public expectedStartTime: LocalTime = LocalTime.MIDNIGHT;
    private _proposedEndTime: LocalTime | null = null;
    /**
    * Proposed Time for the Journey to end, generally when the driver shift finishes. Used for route planning purposes. Assumed to be in the Tenant's Timezone, on the DespatchDate
    */
    public get proposedEndTime(): LocalTime | null { return this._proposedEndTime }
    public set proposedEndTime(value: LocalTime | null) {
      this._proposedEndTime = value === undefined ? null : value;
    }
    /**
    * Driver Name
    * Not Nullable
    * Max Length : 1000
    */
    public driverName: string = "";
    public readOnly: JourneyHeaderReadOnly = new JourneyHeaderReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isActive = jsonSerializedObject.isActive;
          this.isPlanningLocked = jsonSerializedObject.isPlanningLocked;
          this.expectedStartTime = LocalTime.parse(jsonSerializedObject.expectedStartTime);
          this.proposedEndTime = jsonSerializedObject.proposedEndTime == null ? null : LocalTime.parse(jsonSerializedObject.proposedEndTime);
          this.driverName = jsonSerializedObject.driverName;
          this.readOnly = new JourneyHeaderReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isActive = this.isActive;
        toRet.isPlanningLocked = this.isPlanningLocked;
        toRet.expectedStartTime = this.expectedStartTime.toString();
        toRet.proposedEndTime = this.proposedEndTime == null ? null : this.proposedEndTime.toString();
        toRet.driverName = this.driverName;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "JourneyHeader.Id cannot be empty"},
            {field: "driverName", rule: (v: any) => v == null || v.length <= 1000 || "DriverName has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class JourneyFilter extends ModelObject<JourneyFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _fromDespatchDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) despatch date
    */
    public get fromDespatchDate(): LocalDate | null { return this._fromDespatchDate }
    public set fromDespatchDate(value: LocalDate | null) {
      this._fromDespatchDate = value === undefined ? null : value;
    }
    private _toDespatchDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) despatch date
    */
    public get toDespatchDate(): LocalDate | null { return this._toDespatchDate }
    public set toDespatchDate(value: LocalDate | null) {
      this._toDespatchDate = value === undefined ? null : value;
    }
    /**
    * Yes = Journeys which have started only (they may have completed too), No = Not Started Only, Either = Either
    */
    public startedOrNot: EnumYesNoEither = EnumYesNoEither.either;
    /**
    * Yes = Journeys which have ended only, No = Not Ended Only, Either = Either
    */
    public endedOrNot: EnumYesNoEither = EnumYesNoEither.either;
    private _vehicleIds: Guid[] | null = null;
    public get vehicleIds(): Guid[] | null { return this._vehicleIds }
    public set vehicleIds(value: Guid[] | null) {
      this._vehicleIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.fromDespatchDate = jsonSerializedObject.fromDespatchDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDespatchDate);
          this.toDespatchDate = jsonSerializedObject.toDespatchDate == null ? null : LocalDate.parse(jsonSerializedObject.toDespatchDate);
          this.startedOrNot = EnumYesNoEither.getByValue(jsonSerializedObject.startedOrNot, this.startedOrNot);
          this.endedOrNot = EnumYesNoEither.getByValue(jsonSerializedObject.endedOrNot, this.endedOrNot);
          this.vehicleIds = jsonSerializedObject.vehicleIds == null ? null : jsonSerializedObject.vehicleIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.fromDespatchDate = this.fromDespatchDate == null ? null : this.fromDespatchDate.toString();
        toRet.toDespatchDate = this.toDespatchDate == null ? null : this.toDespatchDate.toString();
        toRet.startedOrNot = this.startedOrNot.value;
        toRet.endedOrNot = this.endedOrNot.value;
        toRet.vehicleIds = this.vehicleIds == null ? null : this.vehicleIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredJourneyHeadersResult extends ModelObject<FilteredJourneyHeadersResult> {

    public matchingCount: number = 0;
    public journeyHeaders: JourneyHeader[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.journeyHeaders = jsonSerializedObject.journeyHeaders.map((v: any) => new JourneyHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.journeyHeaders = this.journeyHeaders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "journeyHeaders", rule: (v: any) => !!v || "journeyHeaders is required"},
            {field: "journeyHeaders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as JourneyHeader[]).reduce((acc: Array<string>, curr: JourneyHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class JourneySummaryCalculated extends ModelObject<JourneySummaryCalculated> {

    public numberOfStops: number = 0;
    public totalWeightKg: number = 0;
    public totalDurationSeconds: number = 0;
    public distanceKm: number = 0;
    public polyline: string = "";
    public stopTimeSeconds: number = 0;
    public travelTimeSeconds: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.numberOfStops = jsonSerializedObject.numberOfStops;
          this.totalWeightKg = jsonSerializedObject.totalWeightKg;
          this.totalDurationSeconds = jsonSerializedObject.totalDurationSeconds;
          this.distanceKm = jsonSerializedObject.distanceKm;
          this.polyline = jsonSerializedObject.polyline;
          this.stopTimeSeconds = jsonSerializedObject.stopTimeSeconds;
          this.travelTimeSeconds = jsonSerializedObject.travelTimeSeconds;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.numberOfStops = this.numberOfStops;
        toRet.totalWeightKg = this.totalWeightKg;
        toRet.totalDurationSeconds = this.totalDurationSeconds;
        toRet.distanceKm = this.distanceKm;
        toRet.polyline = this.polyline;
        toRet.stopTimeSeconds = this.stopTimeSeconds;
        toRet.travelTimeSeconds = this.travelTimeSeconds;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "numberOfStops", rule: (v: any) => v == null || v >= -2147483648 || "NumberOfStops is not greater than minimum allowed value: -2147483648"},
            {field: "numberOfStops", rule: (v: any) => v == null || v <= 2147483647 || "NumberOfStops is above maximum allowed value: 2147483647"},
    ];
}


export class StopKey extends ModelObject<StopKey> {

    private _orderId: Guid | null = null;
    /**
    * Order Id
    */
    public get orderId(): Guid | null { return this._orderId }
    public set orderId(value: Guid | null) {
      this._orderId = value === undefined ? null : value;
    }
    private _ullageId: Guid | null = null;
    /**
    * Ullage Id
    */
    public get ullageId(): Guid | null { return this._ullageId }
    public set ullageId(value: Guid | null) {
      this._ullageId = value === undefined ? null : value;
    }
    private _visitId: Guid | null = null;
    /**
    * Visit Id
    */
    public get visitId(): Guid | null { return this._visitId }
    public set visitId(value: Guid | null) {
      this._visitId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.orderId = jsonSerializedObject.orderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.orderId);
          this.ullageId = jsonSerializedObject.ullageId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.ullageId);
          this.visitId = jsonSerializedObject.visitId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.visitId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.orderId = this.orderId == null ? null : this.orderId.toString();
        toRet.ullageId = this.ullageId == null ? null : this.ullageId.toString();
        toRet.visitId = this.visitId == null ? null : this.visitId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "orderId", rule: (v: any) => v == null || !v.isEmpty() || "StopKey.OrderId cannot be empty"},
            {field: "ullageId", rule: (v: any) => v == null || !v.isEmpty() || "StopKey.UllageId cannot be empty"},
            {field: "visitId", rule: (v: any) => v == null || !v.isEmpty() || "StopKey.VisitId cannot be empty"},
    ];
}


export class JourneyDetail extends ModelObject<JourneyDetail> {

    public journey: JourneyHeader = new JourneyHeader();
    public calculated: JourneySummaryCalculated = new JourneySummaryCalculated();
    public orderedStopKeys: StopKey[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.journey = new JourneyHeader(jsonSerializedObject.journey);
          this.calculated = new JourneySummaryCalculated(jsonSerializedObject.calculated);
          this.orderedStopKeys = jsonSerializedObject.orderedStopKeys.map((v: any) => new StopKey(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.journey = this.journey.toJsonSerializedObject();
        toRet.calculated = this.calculated.toJsonSerializedObject();
        toRet.orderedStopKeys = this.orderedStopKeys.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "journey", rule: (v: any) => !!v || "journey is required"},
            {field: "journey", rule: (v: any) => v.validate()},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
            {field: "orderedStopKeys", rule: (v: any) => !!v || "orderedStopKeys is required"},
            {field: "orderedStopKeys", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StopKey[]).reduce((acc: Array<string>, curr: StopKey) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class JourneyService extends ServiceBase {

  public async getJourneyHeadersByFilter(tenantId: Guid, filter: JourneyFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredJourneyHeadersResult> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredJourneyHeadersResult(response.results);;
    return this.makeJsonRequest("journey/v2/getJourneyHeadersByFilter", req, false, extractor, opts);
  };

  public async getJourneyDetailsForDespatchDate(tenantId: Guid, despatchDate: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<JourneyDetail[]> {
    const req = {
    tenantId:tenantId.toString(),
    despatchDate:despatchDate.toString(),
    };
    const extractor = (response:any) => response.journeys.map((v: any) => new JourneyDetail(v));;
    return this.makeJsonRequest("journey/v2/getJourneyDetailsForDespatchDate", req, false, extractor, opts);
  };

  public async getJourneyDetail(tenantId: Guid, journeyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<JourneyDetail> {
    const req = {
    tenantId:tenantId.toString(),
    journeyId:journeyId.toString(),
    };
    const extractor = (response:any) => new JourneyDetail(response.journey);;
    return this.makeJsonRequest("journey/v2/getJourneyDetail", req, false, extractor, opts);
  };

  public async generateDriverManifests(tenantId: Guid, journeyIds: readonly Guid[], documentFormat: EnumDocumentFileType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    journeyIds:journeyIds.map(v => v.toString()),
    documentFormat:documentFormat.value,
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("journey/v2/generateDriverManifests", req, false, extractor, opts);
  };

  public async getJourneyDetails(tenantId: Guid, journeyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<JourneyDetail[]> {
    const req = {
    tenantId:tenantId.toString(),
    journeyIds:journeyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.journeys.map((v: any) => new JourneyDetail(v));;
    return this.makeJsonRequest("journey/v2/getJourneyDetails", req, false, extractor, opts);
  };
}

export const journeyService = new JourneyService();